import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchConferenceSubscriptionTracker } from '../../../services/admin/subscriptionTrackerApi';
import {
  CONFERENCE_TRACKER_SPORTS,
} from '../../../types/admin/subscriptionTracker';
import { getSportLabel } from '../../../utils/admin/formatSubscriptionDate';
import { useAbortableRequest } from '../../../hooks/useAbortableRequest';
import Spinner from '../../Spinner';
import UserSubPagination from './UserSubPagination';
import ConferenceSubTable, { ConferenceSubRow } from './ConferenceSubTable';
import ConferenceTeamsModal from './ConferenceTeamsModal';
import AdminMultiSelect from './AdminMultiSelect';

const DEFAULT_SPORT = 'MFB';
const FETCH_LIMIT = 200;

const CONFERENCE_SPORT_LABELS: Record<string, string> = {
  MFB: "Men's Football",
  MBB: "Men's Basketball",
  WBB: "Women's Basketball",
  MIH: "Men's Ice Hockey",
  WIH: "Women's Ice Hockey",
  MSO: "Men's Soccer",
  WSO: "Women's Soccer",
  MVB: "Men's Volleyball",
  WVB: "Women's Volleyball",
};

function conferenceSportLabel(code: string): string {
  return CONFERENCE_SPORT_LABELS[code] ?? getSportLabel(code);
}

function sortByDidTotalDesc(a: ConferenceSubRow, b: ConferenceSubRow): number {
  if (b.teams_did !== a.teams_did) return b.teams_did - a.teams_did;
  const ratioA = a.teams_total > 0 ? a.teams_did / a.teams_total : 0;
  const ratioB = b.teams_total > 0 ? b.teams_did / b.teams_total : 0;
  if (ratioB !== ratioA) return ratioB - ratioA;
  if (b.teams_total !== a.teams_total) return b.teams_total - a.teams_total;
  return a.conference_name.localeCompare(b.conference_name);
}

const SPORT_OPTIONS = CONFERENCE_TRACKER_SPORTS.map((code) => ({
  value: code,
  label: `${conferenceSportLabel(code)} (${code})`,
}));

const ConferenceSubPage: React.FC = () => {
  const navigate = useNavigate();
  const { start: startListRequest } = useAbortableRequest();

  const [selectedSports, setSelectedSports] = useState<string[]>([DEFAULT_SPORT]);
  const [selectedConferences, setSelectedConferences] = useState<string[]>([]);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(50);

  const [allRows, setAllRows] = useState<ConferenceSubRow[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedConference, setSelectedConference] = useState<ConferenceSubRow | null>(null);

  const activeSports = useMemo(
    () => (selectedSports.length > 0 ? selectedSports : [DEFAULT_SPORT]),
    [selectedSports]
  );

  const handleSportsChange = (next: string[]) => {
    // Keep at least MFB if the user clears everything.
    setSelectedSports(next.length > 0 ? next : [DEFAULT_SPORT]);
    setSelectedConferences([]);
    setSelectedConference(null);
    setOffset(0);
    setAllRows([]);
    setListError(null);
  };

  const handleConferencesChange = (next: string[]) => {
    setSelectedConferences(next);
    setSelectedConference(null);
    setOffset(0);
  };

  const loadList = useCallback(async () => {
    const controller = startListRequest();
    setListLoading(true);
    setListError(null);

    const results = await Promise.all(
      activeSports.map((sportCode) =>
        fetchConferenceSubscriptionTracker(
          {
            sport_code: sportCode,
            limit: FETCH_LIMIT,
            offset: 0,
          },
          controller.signal
        )
      )
    );

    if (controller.signal.aborted || results.some((r) => r.aborted)) return;

    if (results.some((r) => r.status === 401 || r.status === 403)) {
      navigate('/dashboard');
      return;
    }

    const rows: ConferenceSubRow[] = [];
    const errors: string[] = [];

    for (let i = 0; i < results.length; i += 1) {
      const result = results[i];
      const sportCode = activeSports[i];
      if (result.data) {
        for (const conference of result.data.conferences) {
          rows.push({
            ...conference,
            sport_code: result.data.sport_code || sportCode,
          });
        }
      } else if (result.error) {
        errors.push(`${sportCode}: ${result.error}`);
      }
    }

    rows.sort(sortByDidTotalDesc);
    setAllRows(rows);

    if (rows.length === 0 && errors.length > 0) {
      setListError(errors.join(' · '));
    } else if (errors.length > 0) {
      setListError(`Partial load — ${errors.join(' · ')}`);
    } else {
      setListError(null);
    }

    setListLoading(false);
  }, [activeSports, navigate, startListRequest]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const conferenceOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const row of allRows) {
      if (!seen.has(row.conference_name)) {
        seen.set(
          row.conference_name,
          row.conference_short_name && row.conference_short_name !== row.conference_name
            ? `${row.conference_name} (${row.conference_short_name})`
            : row.conference_name
        );
      }
    }
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allRows]);

  // Drop conference filters that no longer exist after a sport change reload.
  useEffect(() => {
    if (selectedConferences.length === 0) return;
    const allowed = new Set(conferenceOptions.map((o) => o.value));
    const next = selectedConferences.filter((name) => allowed.has(name));
    if (next.length !== selectedConferences.length) {
      setSelectedConferences(next);
    }
  }, [conferenceOptions, selectedConferences]);

  const filteredRows = useMemo(() => {
    if (selectedConferences.length === 0) return allRows;
    const selected = new Set(selectedConferences);
    return allRows.filter((row) => selected.has(row.conference_name));
  }, [allRows, selectedConferences]);

  const pageRows = useMemo(
    () => filteredRows.slice(offset, offset + limit),
    [filteredRows, offset, limit]
  );

  const pagination = useMemo(
    () => ({
      limit,
      offset,
      total: filteredRows.length,
      has_more: offset + limit < filteredRows.length,
    }),
    [filteredRows.length, limit, offset]
  );

  const isInitialLoad = listLoading && allRows.length === 0;

  return (
    <div className="p-4 bg-white shadow-md rounded-md mb-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Conference Subscriptions</h2>
        <p className="text-gray-600 text-sm mt-1">
          Defaults to MFB. Sorted by Subscribed/Total descending. Filter by one or more sports and conferences.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap gap-3 items-end">
        <AdminMultiSelect
          label="Sports"
          options={SPORT_OPTIONS}
          selected={selectedSports}
          onChange={handleSportsChange}
          placeholder="Select sports…"
        />

        <AdminMultiSelect
          label="Conferences"
          options={conferenceOptions}
          selected={selectedConferences}
          onChange={handleConferencesChange}
          placeholder="All conferences"
          disabled={listLoading && allRows.length === 0}
          className="flex-1 max-w-md"
        />

        <button
          type="button"
          onClick={() => loadList()}
          disabled={listLoading}
          className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {listError && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{listError}</div>
      )}

      {listLoading && allRows.length > 0 && (
        <div className="mb-3 flex items-center gap-2 text-sm text-gray-600">
          <Spinner size="sm" />
          <span>Updating results…</span>
        </div>
      )}

      {isInitialLoad ? (
        <div className="flex justify-center items-center h-48">
          <Spinner size="md" />
          <span className="ml-2 text-gray-600">Loading conferences…</span>
        </div>
      ) : (
        <>
          <ConferenceSubTable
            conferences={pageRows}
            showSportColumn={activeSports.length > 1}
            onViewTeams={setSelectedConference}
          />
          <UserSubPagination
            pagination={pagination}
            onOffsetChange={setOffset}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setOffset(0);
            }}
          />
        </>
      )}

      <ConferenceTeamsModal
        isOpen={selectedConference != null}
        sportCode={selectedConference?.sport_code ?? DEFAULT_SPORT}
        conferenceName={selectedConference?.conference_name ?? ''}
        conferenceShortName={selectedConference?.conference_short_name}
        onClose={() => setSelectedConference(null)}
      />
    </div>
  );
};

export default ConferenceSubPage;
